import { verifyResultCertificate } from './certificates.js';

const DATABASE_NAME = 'dechess-certificates';
const DATABASE_VERSION = 1;
const STORE_NAME = 'certificates';

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, {
          keyPath: 'certificateHash'
        });
        store.createIndex('endTs', 'endTs');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Wait until the transaction commits — request success alone is not durable. */
function transactionComplete(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
  });
}

/**
 * Plain JSON clone so deep-frozen certificates (and other non-cloneable bits)
 * still persist. Countersigners finalize+freeze before save; a frozen put can
 * fail or commit-race on some browsers and leave only the peer with the wire copy.
 */
export function cloneCertificateForStore(certificate) {
  return JSON.parse(JSON.stringify(certificate));
}

async function withStore(mode, run) {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, mode);
    // Attach completion listeners before awaiting requests so we cannot miss commit.
    const committed = transactionComplete(transaction);
    const result = await run(transaction.objectStore(STORE_NAME), transaction);
    await committed;
    return result;
  } finally {
    database.close();
  }
}

export async function saveCertificate(certificate, verificationOptions = {}) {
  if (!certificate?.certificateHash) {
    throw new Error('Only finalized certificates can be persisted');
  }
  const verification = await verifyResultCertificate(certificate, verificationOptions);
  if (!verification.valid) {
    throw new Error(`Invalid certificate: ${verification.errors.join(', ')}`);
  }

  const stored = cloneCertificateForStore(certificate);
  await withStore('readwrite', async (store) => {
    await requestResult(store.put(stored));
  });
  return stored;
}

export async function getCertificate(hash) {
  if (!hash) {
    return null;
  }
  return withStore('readonly', async (store) => (
    (await requestResult(store.get(hash))) || null
  ));
}

export async function listCertificates({ playerId = null, timeControl = null } = {}) {
  const certificates = await withStore('readonly', async (store) => (
    requestResult(store.index('endTs').getAll())
  ));
  return certificates
    .filter(
      (certificate) =>
        (!playerId
          || certificate.players.some((player) => player.playerId === playerId))
        && (!timeControl || certificate.timeControl === timeControl)
    )
    .sort((left, right) => left.endTs - right.endTs);
}

export async function certificateChainHead(playerId) {
  const certificates = await listCertificates({ playerId });
  if (certificates.length === 0) {
    return null;
  }
  const referenced = new Set(
    certificates
      .map((certificate) => certificate.chainPrev?.[playerId])
      .filter(Boolean)
  );
  const heads = certificates.filter(
    (certificate) => !referenced.has(certificate.certificateHash)
  );
  if (heads.length !== 1) {
    throw new Error(`Certificate chain has ${heads.length} heads`);
  }
  return heads[0].certificateHash;
}
